/* =====================================================================
   UNIQORNIA global scripts. Owned by UNIQORNIA, not by Fabric/Horizon.
   Loaded with defer from snippets/uniq-globals.liquid (part 'head').

   1. Mobile menu drawer repaint fix (iOS WebKit), moved unchanged from the
      live layout/theme.liquid lines 237-290.
   2. addDonationToCart(variantId), moved from the live layout/theme.liquid
      lines 292-317. Called by the inline onclick in
      snippets/cart-donation-upsell.liquid, so it stays a global function.
      Same behaviour as live (POST cart/add.js, then reload), plus a
      response.ok check: on failure the button gets its label back and the
      existing message products.product.add_to_cart_error is shown, read
      from the data-add-to-cart-error attribute of this script's tag.
   3. 4B-4 (Charlotte, 31 Oct 2026, Q35): carousel slides that Horizon 4.3
      marks aria-hidden keep their links out of the Tab order until they
      show.
   4. UNI-12 (audit M4, 2 Nov 2026): the language select changes the page
      only on an explicit choice, not on an arrow key.
   5. UNI-12 (audit M4): focus goes back to the Menu button when the mobile
      menu drawer closes.
   6. UNI-12 (audit M4): after a click-to-load "Play video" button hides
      itself, focus moves to the video player it revealed.
   7. UNI-12 (audit M4): the support-charlotte video window takes focus to
      its Close button and gives it back to the Watch Story button.
   8. UNI-12 (axe aria-allowed-attr, 2 Nov 2026): slideshow dot and
      thumbnail buttons say which slide shows with aria-current, not
      aria-selected.
   Items 4 to 8 add listeners only; items 4 to 7 edit no Horizon file, and
   item 8 goes with a marked edit in snippets/slideshow-controls.liquid.
   ===================================================================== */

/* 1. Mobile menu drawer fix (iOS WebKit repaint).
      Deferred: only needed after the visitor opens the drawer. */
(function() {
  function initDrawerFix() {
    var headerDrawer = document.querySelector('header-drawer');
    if (!headerDrawer) return;

    var mainDetails = headerDrawer.querySelector('details');
    if (!mainDetails) return;

    new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          mutation.target.classList.contains('menu-open')
        ) {
          var target = mutation.target;
          setTimeout(function() {
            var drawer = target.querySelector('.menu-drawer, .menu-drawer__submenu');
            if (!drawer) return;

            // Batch reads first, then writes — avoids forced reflows.
            var elements = drawer.querySelectorAll('.menu-drawer__animated-element, .details-content, li, a, summary');
            var rects = [];
            elements.forEach(function(el) { rects.push(el.getBoundingClientRect()); });
            elements.forEach(function(el) {
              var prev = el.style.webkitTransform;
              el.style.webkitTransform = 'translateZ(0)';
              requestAnimationFrame(function() { el.style.webkitTransform = prev; });
            });
          }, 200);
        }
      });
    }).observe(mainDetails, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });
  }

  // Defer until idle — drawer isn't needed before user interacts.
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(initDrawerFix, { timeout: 3000 });
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDrawerFix);
  } else {
    initDrawerFix();
  }
})();

/* 2. Cart donation (global scope). */
(function() {
  var script = document.currentScript || document.querySelector('script[data-uniq-globals]');
  var addToCartError = (script && script.getAttribute('data-add-to-cart-error')) || '';

  function findErrorNote(btn) {
    var container = btn.closest('.cart-donation-upsell') || btn.parentNode;
    if (!container) return null;
    return { container: container, note: container.querySelector('[data-uniq-donation-error]') };
  }

  function clearDonationError(btn) {
    var found = findErrorNote(btn);
    if (found && found.note) {
      found.note.textContent = '';
      found.note.hidden = true;
    }
  }

  function showDonationError(btn) {
    if (!addToCartError) return;
    var found = findErrorNote(btn);
    if (!found) return;
    var note = found.note;
    if (!note) {
      note = document.createElement('p');
      note.setAttribute('data-uniq-donation-error', '');
      note.setAttribute('role', 'alert');
      found.container.appendChild(note);
    }
    note.hidden = false;
    note.textContent = addToCartError;
  }

  window.addDonationToCart = function(variantId) {
    var btn = document.querySelector('button[data-variant-id="' + variantId + '"]');
    if (!btn) return;
    var originalText = btn.innerHTML;
    btn.innerHTML = '...';
    clearDonationError(btn);

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }]
      })
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('cart/add.js returned ' + response.status);
      }
      window.location.reload();
    })
    .catch(function(error) {
      console.error('Error:', error);
      btn.innerHTML = originalText;
      showDonationError(btn);
    });
  };
})();

/* 3. 4B-4: links in hidden carousel slides leave the Tab order.
      Horizon 4.3's carousels (slideshow-component: the collection and
      product lists, the slideshow section) mark every slide outside the
      view aria-hidden="true" but leave its links and buttons focusable, so
      Tab walks into slides a screen reader is told to skip (axe
      aria-hidden-focus, serious; Step 4B). Live 3.2.1 did the same for a
      keyboard; axe flagged it only on 4.3.
      While a slide is marked hidden, each focusable element in it gets
      tabindex="-1"; its own tabindex, if any, is kept in
      data-uniq-tabindex and given back as soon as 4.3 removes the mark.
      Nothing changes for touch or mouse: swipe, tap and the look stay the
      same. Keyboard users reach the visible cards and the "Next slide"
      button. A MutationObserver follows 4.3's own aria-hidden changes and
      slides added later (section reloads, infinite scroll). */
(function() {
  var SLIDE = 'slideshow-slide, .resource-list__slide';
  var HIDDEN_SLIDE = 'slideshow-slide[aria-hidden="true"], .resource-list__slide[aria-hidden="true"]';
  var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

  // Each element follows every slide around it, so a link in a hidden inner
  // slide (a product card's picture slider) stays out while its outer slide
  // shows.
  function syncSlide(slide) {
    slide.querySelectorAll(FOCUSABLE).forEach(function(el) {
      var hidden = el.closest(HIDDEN_SLIDE) !== null;
      if (hidden && !el.hasAttribute('data-uniq-tabindex')) {
        var own = el.getAttribute('tabindex');
        if (own === '-1') return; // already out of the Tab order: 4.3's own
        el.setAttribute('data-uniq-tabindex', own === null ? '' : own);
        el.setAttribute('tabindex', '-1');
      } else if (!hidden && el.hasAttribute('data-uniq-tabindex')) {
        var before = el.getAttribute('data-uniq-tabindex');
        if (before === '') {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', before);
        }
        el.removeAttribute('data-uniq-tabindex');
      }
    });
  }

  function syncAll(root) {
    root.querySelectorAll(SLIDE).forEach(syncSlide);
  }

  new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes') {
        if (mutation.target.matches(SLIDE)) syncSlide(mutation.target);
        return;
      }
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType !== 1) return;
        if (node.matches(SLIDE)) syncSlide(node);
        syncAll(node);
      });
    });
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['aria-hidden'],
    childList: true,
    subtree: true
  });

  syncAll(document);
})();

/* 4. Language select: no change of page on an arrow key (WCAG 3.2.2).
      Horizon 4.3's language <select> (snippets/localization-form.liquid,
      on:change="/changeLanguage" in assets/localization.js) submits the
      form on every change event. In Chrome, Edge and Firefox on Windows and
      Linux an arrow key, Home, End, Page Up/Down or a typed letter on the
      closed select fires change at once, so a keyboard user browsing the
      list was sent to the other language.
      A window capture listener runs before Horizon's document capture
      listener (assets/component.js). A change that comes from such a key
      press is stopped there; the select keeps the new value on screen.
      Enter then applies it. Choosing from the opened list (mouse, touch,
      or Alt+Down / Space then Enter), and the phone picker, still apply at
      once, as before. Leaving the select without Enter puts the current
      language back, so what it shows is always the page's language. */
(function() {
  var SELECT = 'select[name="language_code"]';
  var KEYS = /^(ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Home|End|PageUp|PageDown)$/;
  var keyedSelect = null;
  var applying = false;

  function isLanguageSelect(el) {
    return el instanceof HTMLSelectElement && el.matches(SELECT);
  }

  function pageLanguage(select) {
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].defaultSelected) return select.options[i].value;
    }
    return select.options.length ? select.options[0].value : '';
  }

  window.addEventListener('keydown', function(event) {
    var select = event.target;
    if (!isLanguageSelect(select)) return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    if (event.key === 'Enter') {
      if (select.value !== pageLanguage(select)) {
        event.preventDefault();
        applying = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        applying = false;
      }
      return;
    }

    if (KEYS.test(event.key) || (event.key && event.key.length === 1 && event.key !== ' ')) {
      // The browser fires change during this key press's default action,
      // in the same task; the flag is gone by the next one.
      keyedSelect = select;
      setTimeout(function() {
        if (keyedSelect === select) keyedSelect = null;
      }, 0);
    }
  }, true);

  window.addEventListener('change', function(event) {
    var select = event.target;
    if (!isLanguageSelect(select) || applying) return;
    if (keyedSelect === select) {
      event.stopImmediatePropagation();
    }
  }, true);

  window.addEventListener('focusout', function(event) {
    var select = event.target;
    if (!isLanguageSelect(select) || applying) return;
    var current = pageLanguage(select);
    if (select.value !== current) select.value = current;
  }, true);
})();

/* 5. Mobile menu drawer: focus back to the Menu button on close.
      Horizon 4.3 (assets/header-drawer.js) closes the drawer on Escape and
      on its Close button, then removes the open attribute, but leaves focus
      on the now hidden button, so it fell to <body> and Tab started again
      from the top of the page. When the main drawer's open attribute goes
      away and focus is on <body> or inside the closed drawer, focus moves
      to the drawer's own <summary> (the Menu button). */
(function() {
  function watch(drawer) {
    var details = drawer.querySelector('details');
    if (!details) return;
    var summary = details.querySelector(':scope > summary');
    if (!summary) return;

    new MutationObserver(function() {
      if (details.hasAttribute('open')) return;
      var active = document.activeElement;
      if (!active || active === document.body || (details.contains(active) && active !== summary)) {
        summary.focus({ preventScroll: true });
      }
    }).observe(details, { attributes: true, attributeFilter: ['open'] });
  }

  document.querySelectorAll('header-drawer').forEach(watch);
})();

/* 6. Click-to-load videos: focus to the player after Play.
      The Play buttons of sections/charlotte-video.liquid
      (.cs-video-play-btn) and sections/making-uniqornia.liquid
      (.cs-making-video__play-btn) hide their cover, and themselves with
      it, and reveal a <video controls> or an <iframe>. Focus then fell to
      <body>. The sections' own click handlers run on the button first; this
      document listener runs after them and moves focus to the revealed
      player, so Tab and Space carry on from there. */
(function() {
  var PLAY = '.cs-video-play-btn, .cs-making-video__play-btn';

  function visible(el) {
    return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  }

  document.addEventListener('click', function(event) {
    var button = event.target instanceof Element ? event.target.closest(PLAY) : null;
    if (!button) return;
    var section = button.closest('[data-section-id]');
    if (!section) return;

    requestAnimationFrame(function() {
      if (visible(button)) return;
      var players = section.querySelectorAll('video, iframe');
      for (var i = 0; i < players.length; i++) {
        var player = players[i];
        if (!visible(player)) continue;
        if (player.tabIndex < 0 && !player.hasAttribute('tabindex')) player.setAttribute('tabindex', '0');
        player.focus({ preventScroll: true });
        return;
      }
    });
  });
})();

/* 7. Support-charlotte video window: focus in and back out.
      assets/support-charlotte.js shows the window (display flex,
      aria-hidden false) and hides it on Close, the backdrop or Escape, but
      left focus on the Watch Story button behind it, and on <body> after
      closing. Here focus moves to the window's Close button when it opens
      and back to the Watch Story button that opened it when it closes. */
(function() {
  var opener = null;

  document.addEventListener('click', function(event) {
    var trigger = event.target instanceof Element ? event.target.closest('[data-video-modal-trigger]') : null;
    if (!trigger) return;
    opener = trigger;
    requestAnimationFrame(function() {
      var open = document.querySelector('[data-support-video-modal][aria-hidden="false"]');
      var close = open ? open.querySelector('.uniq-video-modal__close') : null;
      if (close) close.focus();
    });
  });

  document.querySelectorAll('[data-support-video-modal]').forEach(function(modal) {
    new MutationObserver(function() {
      if (modal.getAttribute('aria-hidden') !== 'true' || !opener) return;
      var back = opener;
      opener = null;
      if (document.contains(back)) back.focus();
    }).observe(modal, { attributes: true, attributeFilter: ['aria-hidden'] });
  });
})();

/* 8. Slideshow dots and thumbnails: aria-current instead of aria-selected.
      aria-selected is not allowed on a <button> (axe aria-allowed-attr,
      critical). snippets/slideshow-controls.liquid (marked UNI-12) now
      renders aria-current="true" on the first dot or thumbnail, and its CSS
      styles [aria-current='true'] like [aria-selected='true']. Horizon 4.3's
      assets/slideshow.js (not edited) still sets aria-selected="true" or
      "false" on every control when the slide changes; this observer turns
      that into aria-current="true" on the current control (removed from the
      others) and removes aria-selected. Only .slideshow-control buttons are
      touched: tabs, list options and the zoom dialog keep their own
      attributes. */
(function() {
  var CONTROL = 'button.slideshow-control';

  function convert(el) {
    if (!el.hasAttribute('aria-selected')) return;
    if (el.getAttribute('aria-selected') === 'true') {
      el.setAttribute('aria-current', 'true');
    } else {
      el.removeAttribute('aria-current');
    }
    el.removeAttribute('aria-selected');
  }

  function convertAll(root) {
    root.querySelectorAll(CONTROL + '[aria-selected]').forEach(convert);
  }

  new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes') {
        if (mutation.target.matches(CONTROL)) convert(mutation.target);
        return;
      }
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType !== 1) return;
        if (node.matches(CONTROL)) convert(node);
        convertAll(node);
      });
    });
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['aria-selected'],
    childList: true,
    subtree: true
  });

  convertAll(document);
})();
